import { useState, type FormEvent } from 'react';
import { confirmDialog } from '@core/ui/confirm';
import { useCanWrite } from '@core/auth/access';
import { Modal, TabPanels } from '@core/ui/Modal';
import { SegmentedControl } from '@core/ui/SegmentedControl';
import { ObjectHistory } from '../../history/ObjectHistory';
import { Button } from '@core/ui/Button';
import { DeleteConfirm, FormError, ModalFooter, fieldInput as input, fieldLabel as label, readOnlyFields, submitOnCtrlEnter } from '@core/ui/form';
import { cn } from '@core/ui/cn';
import { data, useTable } from '@core/store';
import type { Change, Row } from '@core/store/model';
import { getDate } from 'date-fns';
import { fromISO } from '@core/dates';
import { EVENT_DEFAULT_COLOR } from './data';
import { WEEKDAY_NAMES, membersOf, ordinalLabel, planConfirm, planEventSave, planSeriesDelete, ruleDefault, ruleLabel, weekdayName, weekdayOrdinal, type EventFields, type Rule, type Unit } from './series';

const FORM_ID = 'event-form'; // Save живёт в футере окна, вне формы

type Event = Row<'event'>;
export type EventDraft = { id: string | null; name: string; sublabel: string; start_date: string; end_date: string; color: string };

export const eventDraftOf = (e: Event): EventDraft => ({
  id: e.id, name: e.name, sublabel: e.sublabel ?? '', start_date: e.start_date, end_date: e.end_date,
  color: e.color ?? EVENT_DEFAULT_COLOR,
});
export const newEventDraft = (start: string, end: string): EventDraft => ({ id: null, name: '', sublabel: '', start_date: start, end_date: end, color: EVENT_DEFAULT_COLOR });

const fieldsOf = (d: EventDraft): EventFields => ({ name: d.name.trim(), sublabel: d.sublabel.trim() || null, start_date: d.start_date, end_date: d.end_date, color: d.color });

// Событие на шкале: турнир, конференция, релиз. Нерабочие дни — календарь в коде (HOLIDAYS), не события.
// С расписанием — серия: это событие первый экземпляр, остальные рождаются неподтверждёнными (пунктир).
export function EventModal({ draft, onClose }: { draft: EventDraft; onClose: () => void }) {
  const canWrite = useCanWrite();
  const events = useTable('event');
  const seriesTable = useTable('event_series');
  const [d, setD] = useState(draft);
  const [tab, setTab] = useState<'main' | 'history'>('main');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const live = d.id ? events.get(d.id) : undefined;
  const series = live?.series_id ? seriesTable.get(live.series_id) : undefined;
  const members = series ? membersOf(series.id, events.values()) : [];
  const [rule, setRule] = useState<Rule | null>(() => (series ? (series.rule as Rule) : null));

  async function run(changes: Change[] | { error: string }, kind: string) {
    if ('error' in changes) { setError(changes.error); return; }
    setBusy(true); setError(null);
    const r = changes.length ? await data.applyChanges(changes, { kind }) : { ok: true as const };
    setBusy(false);
    if (!r.ok) { setError(r.error); return; }
    onClose();
  }

  function save(e: FormEvent) {
    e.preventDefault();
    if (!d.name.trim()) { setError('Name is required'); return; }
    if (d.end_date < d.start_date) { setError('End is before start'); return; }
    void run(planEventSave(d.id, fieldsOf(d), live, series, members, rule, () => crypto.randomUUID()), d.id ? 'edit_event' : 'create_event');
  }

  // закрытие с несохранёнными правками (Escape, клик вне, Cancel) — спросить; Save и Delete закрывают сами
  const dirty = canWrite && (JSON.stringify(fieldsOf(d)) !== JSON.stringify(fieldsOf(draft)) || JSON.stringify(rule) !== JSON.stringify(series ? series.rule : null));
  const close = async () => {
    if (dirty && !(await confirmDialog({ title: 'Discard changes?', text: 'The event has unsaved changes.', yes: 'Discard', danger: true }))) return;
    onClose();
  };
  const unconfirmed = members.filter((e) => !e.confirmed).length;
  const footer = (
    <ModalFooter form={FORM_ID} busy={busy} onCancel={() => void close()} readOnly={!canWrite} left={d.id && (
      <DeleteConfirm label="Delete" question="Delete this event?" busy={busy} onDelete={() => void run([{ table: 'event', id: d.id!, delete: true }], 'delete_event')}>
        {series && <DeleteConfirm label="Delete series" question={<>Delete the series with its {unconfirmed} unconfirmed {unconfirmed === 1 ? 'event' : 'events'}? Confirmed ones stay.</>} busy={busy} onDelete={() => void run(planSeriesDelete(series, members), 'delete_series')} />}
      </DeleteConfirm>
    )} />
  );
  const form = (
        <form id={FORM_ID} onSubmit={save} onKeyDown={canWrite ? submitOnCtrlEnter : undefined} className="flex flex-col gap-4">
          <label className={label}>Name
            <input className={input} value={d.name} autoFocus required onChange={(e) => setD({ ...d, name: e.target.value })} placeholder="e.g. WSOP" />
          </label>
          <label className={label}>Note
            <input className={input} value={d.sublabel} onChange={(e) => setD({ ...d, sublabel: e.target.value })} placeholder="optional" />
          </label>
          <div className="grid grid-cols-2 gap-4">
            <label className={label}>From
              <input type="date" className={input} value={d.start_date} required onChange={(e) => e.target.value && setD({ ...d, start_date: e.target.value, end_date: e.target.value > d.end_date ? e.target.value : d.end_date })} />
            </label>
            <label className={label}>To
              <input type="date" className={input} value={d.end_date} min={d.start_date} required onChange={(e) => e.target.value && setD({ ...d, end_date: e.target.value })} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <label className={label}>Colour
              <input type="color" className="h-11 w-full border border-line rounded-md bg-paper" value={d.color} onChange={(e) => setD({ ...d, color: e.target.value })} />
            </label>
          </div>
          <div className="flex flex-col gap-3 rounded-md border border-line p-3" data-repeat>
            <label className="flex items-center gap-2 font-medium">
              <input type="checkbox" className="w-5 h-5" checked={!!rule} onChange={(e) => setRule(e.target.checked ? ruleDefault(d.start_date) : null)} />
              Repeat
            </label>
            {rule && <RepeatEditor rule={rule} start={d.start_date} onChange={setRule} />}
            {series && live && (
              <div className="flex items-center gap-2 text-sm text-gray-600 flex-wrap" data-series-info>
                <span>Part of a series · {members.length} {members.length === 1 ? 'event' : 'events'} · {live.confirmed ? 'confirmed' : 'not confirmed yet'}</span>
                {!live.confirmed && <Button variant="secondary" size="sm" className="ml-auto" disabled={busy} onClick={() => void run(planConfirm(live), 'confirm_event')} title="Keep this one as it is: the series won't move or rename it">Confirm</Button>}
              </div>
            )}
            {rule && <p className="text-sm text-gray-600">Repeats {ruleLabel(rule, d.start_date)}. Unconfirmed events follow the series; confirmed ones stay as they are.</p>}
          </div>
          <FormError error={error} />
        </form>
  );
  const head = d.id ? <SegmentedControl role="tabs" label="Event card" className="mb-4" value={tab} onChange={setTab} items={[{ key: 'main', label: 'Event' }, { key: 'history', label: 'History' }]} /> : undefined;
  return (
    <Modal open onClose={() => void close()} title={d.id ? 'Event' : 'New event'} head={head} footer={footer}>
      {d.id ? <TabPanels active={tab} panels={[['main', readOnlyFields(!canWrite, form)], ['history', <ObjectHistory table="event" id={d.id} active={tab === 'history'} />]]} /> : readOnlyFields(!canWrite, form)}
    </Modal>
  );
}

const UNITS: Unit[] = ['day', 'week', 'month', 'year'];
const small = 'min-h-0 border border-line rounded-md px-2 py-1 text-base bg-paper';

/** Составитель расписания, как в календарях: every N unit, дни недели, для месяца число или день недели, конец обязателен. */
function RepeatEditor({ rule, start, onChange }: { rule: Rule; start: string; onChange: (r: Rule) => void }) {
  const set = (patch: Partial<Rule>) => onChange({ ...rule, ...patch });
  const days = rule.weekdays ?? [];
  return (
    <div className="flex flex-col gap-2 text-base" data-repeat-editor>
      <div className="flex items-center gap-2 flex-wrap">
        Every
        <input type="number" min={1} max={99} className={cn(small, 'w-16')} value={rule.every} aria-label="Every" onChange={(e) => set({ every: Number(e.target.value) })} />
        <select className={small} value={rule.unit} aria-label="Unit" onChange={(e) => set({ unit: e.target.value as Unit })}>
          {UNITS.map((u) => <option key={u} value={u}>{rule.every === 1 ? u : `${u}s`}</option>)}
        </select>
        {rule.unit === 'month' && (
          <select className={small} value={rule.monthly ?? 'day'} aria-label="Monthly on" onChange={(e) => set({ monthly: e.target.value as Rule['monthly'] })}>
            <option value="day">on day {getDate(fromISO(start))}</option>
            <option value="weekday">on the {ordinalLabel(weekdayOrdinal(start))} {weekdayName(start)}</option>
          </select>
        )}
      </div>
      {rule.unit === 'week' && (
        <div className="flex gap-1" role="group" aria-label="Weekdays">
          {WEEKDAY_NAMES.map((n, i) => {
            const wd = i + 1; const on = days.includes(wd);
            return (
              <button key={n} type="button" aria-pressed={on} data-weekday={wd} onClick={() => set({ weekdays: on ? days.filter((x) => x !== wd) : [...days, wd] })}
                className={cn('min-h-0 w-11 h-8 rounded-md border text-sm font-semibold', on ? 'bg-accent text-white border-accent' : 'bg-paper border-line text-gray-600 hover:bg-ground')}>{n}</button>
            );
          })}
        </div>
      )}
      <div className="flex items-center gap-3 flex-wrap">
        <label className="flex items-center gap-2">
          <input type="radio" name="repeat-end" checked={rule.end.kind === 'count'} onChange={() => set({ end: { kind: 'count', count: 4 } })} />
          after
          <input type="number" min={2} max={100} className={cn(small, 'w-16')} aria-label="Times" disabled={rule.end.kind !== 'count'} value={rule.end.kind === 'count' ? rule.end.count : 4}
            onChange={(e) => set({ end: { kind: 'count', count: Number(e.target.value) } })} />
          times
        </label>
        <label className="flex items-center gap-2">
          <input type="radio" name="repeat-end" checked={rule.end.kind === 'date'} onChange={() => set({ end: { kind: 'date', date: rule.end.kind === 'date' ? rule.end.date : start } })} />
          until
          <input type="date" className={small} aria-label="Until" min={start} disabled={rule.end.kind !== 'date'} value={rule.end.kind === 'date' ? rule.end.date : start}
            onChange={(e) => e.target.value && set({ end: { kind: 'date', date: e.target.value } })} />
        </label>
      </div>
    </div>
  );
}
